import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, Table, Badge, Alert, Spinner, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import axios from '../../api/Axios';

const ManageTutors = () => {
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [providerCode, setProviderCode] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Get provider code from localStorage
    const code = localStorage.getItem('provider_code');
    setProviderCode(code);
    
    if (code) {
      fetchTutors(code);
    } else {
      setError('Provider code not found. Please log in again.');
      setLoading(false);
    }
  }, []);

  const fetchTutors = async (code) => {
    try {
      setLoading(true);
      const response = await axios.get(`/accounts/tutors/?provider_code=${code}`);
      setTutors(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching tutors:', err);
      setError('Failed to load tutors. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (regStatus) => {
    const statusMap = {
      0: { color: 'warning', text: 'Pending' },
      1: { color: 'info', text: 'Documents Pending' },
      2: { color: 'success', text: 'Verified' },
      3: { color: 'success', text: 'Approved' },
      4: { color: 'success', text: 'Active' },
      5: { color: 'success', text: 'Fully Registered' }
    };
    const status = statusMap[regStatus] || { color: 'secondary', text: 'Unknown' };
    return <Badge color={status.color}>{status.text}</Badge>;
  };

  const handleViewTutor = (tutorId) => {
    navigate(`/tutor-provider-dashboard/tutors/${tutorId}`);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-3 text-muted">Loading tutors...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="danger">
        <i className="bi bi-exclamation-triangle me-2"></i>
        {error}
      </Alert>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0">Manage Tutors</h2>
          {providerCode && (
            <small className="text-muted">
              Provider Code: <code className="text-primary">{providerCode}</code>
            </small>
          )}
        </div>
        <Badge color="info" pill style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
          {tutors.length} Tutor{tutors.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Main table card */}
      <Card>
        <CardBody>
          {tutors.length === 0 ? (
            <Alert color="info">
              <i className="bi bi-info-circle me-2"></i>
              No tutors found. Tutors registered with your provider code will appear here.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Gender</th>
                    <th>Category</th>
                    <th>Qualification</th>
                    <th className="text-center">Qualifications</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tutors.map((tutor) => (
                    <tr key={tutor.id}>
                      <td>
                        <strong>{tutor.full_name}</strong>
                      </td>
                      <td>
                        <small>{tutor.email || 'N/A'}</small>
                      </td>
                      <td>
                        <small>{tutor.phone_number || 'N/A'}</small>
                      </td>
                      <td>
                        <small>{tutor.gender || 'N/A'}</small>
                      </td>
                      <td>
                        <Badge color={tutor.subject_category === 'STEM' ? 'info' : 'warning'}>
                          {tutor.subject_category}
                        </Badge>
                      </td>
                      <td>
                        <small>{tutor.study_level || 'N/A'}</small>
                      </td>
                      <td className="text-center">
                        <Badge color="primary" pill>
                          {tutor.qualification_count || 0}
                        </Badge>
                      </td>
                      <td className="text-center">
                        {getStatusBadge(tutor.reg_status)}
                      </td>
                      <td className="text-center">
                        <UncontrolledDropdown>
                          <DropdownToggle 
                            size="sm" 
                            color="outline-primary"
                          >
                            ⋯
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem onClick={() => handleViewTutor(tutor.id)}>
                              <i className="fas fa-eye me-2"></i>
                              View
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default ManageTutors;